import { useCallback } from "react";
import { useAppRuntime } from "../../../providers/AppRuntimeProvider";
import type { SourceStatusListResponse } from "../../../api/sourceStatus";
import type { SourceAuthConfig, SourceListResponse } from "../types";

export interface IntegrationsApi {
  readonly getSourceList: () => Promise<SourceListResponse>;
  readonly getSourceAuthConfig: (sourceId: string) => Promise<SourceAuthConfig>;
  readonly sourceAuthConnect: (
    sourceId: string,
    params: Record<string, unknown>,
  ) => Promise<unknown>;
  /** §3 L4: the ONLY status wire method — the UI renders this verbatim. */
  readonly getSourceStatus: () => Promise<SourceStatusListResponse>;
  /** §6: one disconnect cascade for every auth kind. */
  readonly disconnectAccount: (sourceId: string, accountId: string) => Promise<void>;
}

export function useIntegrationsApi(): IntegrationsApi {
  const runtime = useAppRuntime();

  const getSourceList = useCallback(() => {
    return runtime.transport.rpc<SourceListResponse>("source.list");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSourceAuthConfig = useCallback((sourceId: string) => {
    return runtime.transport.rpc<SourceAuthConfig>("source.auth.config", {
      source_id: sourceId,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sourceAuthConnect = useCallback(
    (sourceId: string, params: Record<string, unknown>) => {
      return runtime.transport.rpc("source.auth.connect", {
        source_id: sourceId,
        ...params,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const getSourceStatus = useCallback(() => {
    return runtime.transport.rpc<SourceStatusListResponse>("source.status.list");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disconnectAccount = useCallback(
    async (sourceId: string, accountId: string) => {
      await runtime.transport.rpc("source.accounts.disconnect", {
        source_id: sourceId,
        account_id: accountId,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return {
    getSourceList,
    getSourceAuthConfig,
    sourceAuthConnect,
    getSourceStatus,
    disconnectAccount,
  };
}
